import { useEffect } from 'react'
import { readStoredJson, writeStoredJson, removeStored } from '../utils/safeStorage'

// Replace these with your actual values from tawk.to dashboard
// Go to: Administration → Chat Widget → Direct Chat Link
const TAWK_PROPERTY_ID = '69e7c1450121e01c2e370159'
const TAWK_WIDGET_ID   = '1jmoklueo'

export default function TawkChat({ shop, isAdminView }) {
  useEffect(() => {
    // Never show chat widget in admin impersonation mode
    if (isAdminView) {
      if (window.Tawk_API?.hideWidget) window.Tawk_API.hideWidget()
      return
    }

    // If already loaded, just make sure it's visible
    if (window.Tawk_API?.showWidget) {
      window.Tawk_API.showWidget()
      return
    }

    window.Tawk_API       = window.Tawk_API || {}
    window.Tawk_LoadStart = new Date()

    window.Tawk_API.onLoad = function () {
      if (shop) {
        window.Tawk_API.setAttributes({ shop }, () => {})
        window.Tawk_API.visitor = { name: shop }
      }
      window.Tawk_API.showWidget()
    }

    // Someone mid-conversation is scrolling the transcript, not the page, and
    // fading the panel they are reading would be the opposite of helpful.
    const openClass = on => document.body.classList.toggle('sb-chat-open', on)
    window.Tawk_API.onChatMaximized = () => openClass(true)
    window.Tawk_API.onChatMinimized = () => openClass(false)
    window.Tawk_API.onChatEnded     = () => openClass(false)

    // Shrink the launcher and keep it out of the way.
    //
    // Tawk injects its own iframe at a fixed size in the bottom-right corner,
    // over whatever the app has there. At full size it covered buttons behind
    // it and swallowed the clicks. Scaling from the bottom-right corner keeps
    // it pinned where people expect while giving that space back, and the
    // pointer-events rule means only the bubble itself takes clicks rather than
    // the transparent box around it.
    //
    // Their markup, so this is matched several ways: the id and class they use
    // today, and the iframe title as a fallback if those are renamed.
    const css = document.createElement('style')
    css.setAttribute('data-tawk-size', '')
    css.textContent = `
      /* Pinned, not merely scaled.
         Tawk positions its container with inline styles and rewrites them —
         after load, when the page scrolls, and whenever it swaps between the
         minified bubble and the expanded panel, which are different elements
         with different offsets. Scaling alone left the position to them, so
         the bubble drifted and sometimes landed over the table: the transform
         origin is the container's own bottom-right corner, so every time they
         resized the container the scaled bubble moved with it.
         Anchoring both containers to the same place means their inline
         changes have nothing left to move. Declaration-level !important beats
         an inline style, and Tawk's are not themselves !important. */
      #tawkchat-container,
      #tawkchat-minified-wrapper,
      .tawk-min-container {
        position: fixed !important;
        /* Driven by custom properties so the seller can move it. The
           defaults are the corner it has always been in, so nothing changes
           until they drag it somewhere. */
        right: var(--sb-chat-right, 16px) !important;
        bottom: var(--sb-chat-bottom, 16px) !important;
        left: auto !important;
        top: auto !important;
        margin: 0 !important;
      }

      /* The grip the seller drags.
         It has to be ours, not Tawk's: the launcher is drawn inside a
         cross-origin iframe, so a pointer press on the bubble itself is
         delivered to that document and never reaches this page. A small
         handle of our own, sitting just outside the bubble, is the only
         thing this page can actually get hold of.
         Invisible until the pointer is near, so it costs nothing visually
         and the bubble still looks like a bubble. */
      #sb-chat-grip {
        position: fixed;
        width: 22px; height: 22px;
        border-radius: 50%;
        background: var(--bg2, #fff);
        border: 1px solid var(--border, #d7d7d7);
        box-shadow: 0 1px 4px rgba(0,0,0,.18);
        color: var(--text3, #777);
        font-size: 11px; line-height: 20px; text-align: center;
        cursor: grab;
        z-index: 9001;
        opacity: 0;
        transition: opacity .15s;
        user-select: none;
        touch-action: none;
      }
      #sb-chat-grip:hover, #sb-chat-grip.sb-near, #sb-chat-grip.sb-dragging {
        opacity: 1;
      }
      #sb-chat-grip.sb-dragging { cursor: grabbing; }
      /* While dragging, this covers the page so the pointer cannot be
         swallowed by the chat iframe the moment it passes over it. */
      #sb-chat-dragmask {
        position: fixed; inset: 0; z-index: 9000; cursor: grabbing;
      }
      #tawkchat-minified-wrapper,
      .tawk-min-container,
      iframe[title="chat widget"] {
        transform: scale(.72) !important;
        transform-origin: 100% 100% !important;
      }
      /* Under the app's own overlays. Tawk ships a z-index in the
         two-billions, which put the bubble on top of modals and the toast
         stack — so a dialog could be covered by a chat launcher. */
      #tawkchat-container, #tawkchat-minified-wrapper, .tawk-min-container {
        z-index: 9000 !important;
      }
      /* The container is larger than the bubble drawn inside it; without this
         the empty margin around it keeps taking clicks meant for the page. */
      #tawkchat-container { pointer-events: none !important; }
      #tawkchat-container iframe { pointer-events: auto !important; }

    `
    document.head.appendChild(css)

    // ── Letting the seller put it where they want ──────────────────────
    //
    // Where it belongs depends on the screen and on what they are doing, and
    // no default is right for everyone: bottom-right covers the row actions,
    // bottom-left covers the sidebar on narrow windows. So it is draggable,
    // and where they leave it is where it stays.
    //
    // Remembered per browser, which is the right scope for a preference
    // about a screen rather than about the shop. Wrapped, because storage
    // throws in a private window and an unusable chat button is a worse
    // outcome than a forgotten position.
    const KEY = 'sb.chat.pos'
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
    const applyPos = (right, bottom) => {
      document.documentElement.style.setProperty('--sb-chat-right', `${right}px`)
      document.documentElement.style.setProperty('--sb-chat-bottom', `${bottom}px`)
    }
    let pos = { right: 16, bottom: 16 }
    try {
      const saved = readStoredJson(KEY, null)
      if (saved && Number.isFinite(saved.right) && Number.isFinite(saved.bottom)) {
        pos = {
          // Re-clamped on load: a position saved on a wide monitor would put
          // the bubble off-screen on a laptop, with no way to drag it back.
          right:  clamp(saved.right,  0, Math.max(0, window.innerWidth  - 60)),
          bottom: clamp(saved.bottom, 0, Math.max(0, window.innerHeight - 60)),
        }
      }
    } catch { /* private window, or corrupt value — the default is fine */ }
    applyPos(pos.right, pos.bottom)

    // A mask left over from a drag that was interrupted — a reload mid-drag,
    // a pointerup the window never saw — covers the whole page at z-index
    // 9000 and silently blocks every click and scroll underneath it. Cheap
    // to clear, and impossible to diagnose from the outside if it is not.
    document.getElementById('sb-chat-dragmask')?.remove()

    const grip = document.createElement('div')
    grip.id = 'sb-chat-grip'
    grip.textContent = '⠿'
    grip.title = 'Drag to move the chat button — double-click to reset'
    grip.setAttribute('role', 'button')
    grip.setAttribute('aria-label', 'Move the chat button')
    document.body.appendChild(grip)

    // Parked just above-left of the bubble, following it as it moves.
    const placeGrip = () => {
      grip.style.right  = `${pos.right + 44}px`
      grip.style.bottom = `${pos.bottom + 44}px`
    }
    placeGrip()

    // Shown when the pointer is anywhere near that corner, so it can be
    // found without knowing it is there, and hidden the rest of the time so
    // the page is not decorated with a handle nobody is using.
    const onMove = e => {
      const nearX = window.innerWidth  - e.clientX
      const nearY = window.innerHeight - e.clientY
      const near = Math.abs(nearX - pos.right) < 140 && Math.abs(nearY - pos.bottom) < 140
      grip.classList.toggle('sb-near', near)
    }
    window.addEventListener('pointermove', onMove, { passive: true })

    let mask = null
    const onDown = e => {
      e.preventDefault()
      const startX = e.clientX, startY = e.clientY
      const from = { ...pos }
      grip.classList.add('sb-dragging')
      // Covers the page, including the chat iframe. Without it the pointer
      // is captured by that document the instant it crosses the bubble and
      // the drag stops dead halfway.
      mask = document.createElement('div')
      mask.id = 'sb-chat-dragmask'
      document.body.appendChild(mask)

      const move = ev => {
        pos = {
          right:  clamp(from.right  + (startX - ev.clientX), 0, window.innerWidth  - 60),
          bottom: clamp(from.bottom + (startY - ev.clientY), 0, window.innerHeight - 60),
        }
        applyPos(pos.right, pos.bottom)
        placeGrip()
      }
      const up = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        window.removeEventListener('pointercancel', up)
        grip.classList.remove('sb-dragging')
        mask?.remove(); mask = null
        writeStoredJson(KEY, pos)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
      // A touch drag that leaves the screen, or a pen lifted out of range,
      // ends the drag rather than leaving the mask over the whole page.
      window.addEventListener('pointercancel', up)
    }
    grip.addEventListener('pointerdown', onDown)

    // Back to the corner, for anyone who has dragged it somewhere unhelpful.
    const onReset = () => {
      pos = { right: 16, bottom: 16 }
      applyPos(pos.right, pos.bottom)
      placeGrip()
      removeStored(KEY)
    }
    grip.addEventListener('dblclick', onReset)

    // A window that shrank can leave it off-screen and unreachable.
    const onResize = () => {
      pos = {
        right:  clamp(pos.right,  0, Math.max(0, window.innerWidth  - 60)),
        bottom: clamp(pos.bottom, 0, Math.max(0, window.innerHeight - 60)),
      }
      applyPos(pos.right, pos.bottom)
      placeGrip()
    }
    window.addEventListener('resize', onResize)

    // Scrolling is "working"; a short pause is "finished".
    //
    // 600ms, because a mouse wheel arrives as a burst of separate events and
    // anything much shorter makes the bubble flicker between them. Passive,
    // so this cannot slow the scroll it is listening to.
    // Applied to the element, not through a class on <body>.
    //
    // The class-and-selector version did nothing visible. Tawk renames its
    // containers between builds and runs inside an iframe this page cannot
    // style, so a stylesheet that names them is a guess that silently stops
    // matching. Setting opacity on whatever element is actually there cannot
    // miss, and if Tawk has rendered nothing yet there is simply nothing to
    // fade.
    const launchers = () => [
      document.getElementById('tawkchat-minified-wrapper'),
      document.querySelector('.tawk-min-container'),
      document.querySelector('iframe[title="chat widget"]'),
      document.getElementById('tawkchat-container'),
    ].filter(Boolean)

    const setFaded = faded => {
      // Never while the panel is open: that person is reading the transcript,
      // and scrolling it would be the one moment fading is wrong.
      if (document.body.classList.contains('sb-chat-open')) faded = false
      for (const el of launchers()) {
        el.style.setProperty('opacity', faded ? '.18' : '1', 'important')
        el.style.setProperty('transition', 'opacity .18s ease', 'important')
        // Clicks pass through while faded, so it cannot swallow a press
        // meant for the row underneath it.
        if (el.id !== 'tawkchat-container') {
          el.style.setProperty('pointer-events', faded ? 'none' : 'auto', 'important')
        }
      }
    }

    let idle = null
    const onScroll = () => {
      setFaded(true)
      clearTimeout(idle)
      idle = setTimeout(() => setFaded(false), 600)
    }
    // scroll covers the page and any inner container; wheel and touchmove
    // cover the gesture even where it scrolls nothing, which is what happens
    // at the end of a list — exactly where the bubble is in the way.
    for (const ev of ['scroll', 'wheel', 'touchmove']) {
      document.addEventListener(ev, onScroll, { passive: true, capture: true })
    }

    // Keep the stylesheet last, so it keeps winning.
    //
    // Tawk appends its own <style> blocks as it loads and as the widget
    // changes state. A later rule of equal specificity wins, so a stylesheet
    // added before theirs stops applying the moment they add one — which is
    // exactly the "it moves when I click something" case. Moving ours back to
    // the end of <head> when they touch it costs nothing and is not a loop:
    // re-appending an element that is already last does not fire the observer
    // again.
    const keepLast = new MutationObserver(() => {
      if (document.head.lastElementChild !== css) document.head.appendChild(css)
    })
    keepLast.observe(document.head, { childList: true })

    const s = document.createElement('script')
    s.async = true
    s.src   = `https://embed.tawk.to/${TAWK_PROPERTY_ID}/${TAWK_WIDGET_ID}`
    s.charset = 'UTF-8'
    s.setAttribute('crossorigin', '*')
    document.head.appendChild(s)

    return () => {
      if (window.Tawk_API?.hideWidget) window.Tawk_API.hideWidget()
      keepLast.disconnect()
      for (const ev of ['scroll', 'wheel', 'touchmove']) {
        document.removeEventListener(ev, onScroll, { capture: true })
      }
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('resize', onResize)
      clearTimeout(idle)
      document.body.classList.remove('sb-chat-open')
      setFaded(false)
      grip.remove()
      mask?.remove()
      css.remove()
    }
  }, [])

  // When shop changes, update visitor info
  useEffect(() => {
    if (shop && window.Tawk_API?.setAttributes) {
      window.Tawk_API.setAttributes({ shop }, () => {})
    }
  }, [shop])

  return null // widget is injected into the page, no DOM needed
}
